export const CHAT_UUID = import.meta.env.VITE_8x8_UUID || '17826070946aa81f99408563.40038304'
export const UNAUTHENTICATED_CHAT_UUID = import.meta.env.VITE_8x8_UNAUTHENTICATED_UUID || '6033627066a9ab7f3433cf1.51101746'
export const CHAT_BUTTON_CONTAINER_ID = `__8x8-chat-button-container-script_${CHAT_UUID}`
export const UNAUTHENTICATED_CHAT_BUTTON_CONTAINER_ID = `__8x8-chat-button-container-script_${UNAUTHENTICATED_CHAT_UUID}`
export const CHAT_SCRIPT_UUID = `script_${CHAT_UUID}`
export const UNAUTHENTICATED_CHAT_SCRIPT_UUID = `script_${UNAUTHENTICATED_CHAT_UUID}`

export const CHAT_CHANNEL_NAME = import.meta.env.VITE_8X8_CHAT_CHANNEL_NAME || 'Meshify Web Chat Authenticated'
export const CHAT_CHANNEL_UUID = import.meta.env.VITE_8X8_CHAT_CHANNEL_UUID || 'gwoklWjNRX-65_YoJoTxUA'
export const UNAUTHENTICATED_CHAT_CHANNEL_NAME = import.meta.env.VITE_8X8_UNAUTHENTICATED_CHAT_CHANNEL_NAME || 'Meshify Web Chat Unauthenticated'
export const UNAUTHENTICATED_CHAT_CHANNEL_UUID = import.meta.env.VITE_8X8_UNAUTHENTICATED_CHAT_CHANNEL_UUID || 'ZPvJL_QkTGWpUOKCZeYPsw'

export const SHOW_8X8_CHAT_CLASS = 'show-8x8-chat'

const CHAT_TENANT = 'bXVuaWNocmVoc2IwMQ'
const DEFAULT_CHAT_DOMAIN = 'https://vcc-na3.8x8.com'
const CHAT_DISCOVERY_URL = 'https://cloud8-cc-geo.8x8.com/vcc-chat-channels/public/webchat/discovery'

export interface Chat8x8App {
  startChat: () => void
  endChat: () => void
  setCustomerInfo: (customerInfo: Record<string, string>) => void
}

export interface Chat8x8Options {
  channelName: string
  channelUuid: string
  scriptUuid?: string
  buttonContainerId?: string
  prefilledVars?: Record<string, string>
  loaderURL?: string
}

interface Chat8x8Config extends Chat8x8Options {
  scriptUuid: string
  tenant: string
  domain: string
  buttonContainerId: string
  align: string
  themeCustom: Record<string, string>
}

type ReadyCallback = (chatApp: Chat8x8App) => void
type ErrorCallback = (error: Event | Error) => void

let chatApp: Chat8x8App | undefined
let chatButtonContainer: Element | null = null
let isChatActive = false
let isLoading = false
let loadOptions: Partial<Chat8x8Options> = {}
const readyCallbacks: ReadyCallback[] = []
const errorCallbacks: ErrorCallback[] = []

const getButtonContainerId = () => loadOptions.buttonContainerId || CHAT_BUTTON_CONTAINER_ID

// Checks whether 8x8 has rendered its launcher button into the hidden container.
export const has8x8ChatButton = () => Boolean(document.getElementById(getButtonContainerId())?.querySelector('button'))

// Reports whether a chat session is currently active (started and not yet ended).
export const is8x8ChatActive = () => isChatActive

// Chat is usable if 8x8 can render its launcher, or a session from a previous container is still active.
const isChatUsable = () => has8x8ChatButton() || isChatActive

// Watches the launcher container and reports availability whenever its DOM changes.
export const watch8x8ChatAvailability = (onChange: (chatButtonAvailable: boolean) => void) => {
  const container = document.getElementById(getButtonContainerId())

  if (!container) {
    onChange(isChatActive)
    return () => undefined
  }

  const updateAvailability = () => onChange(isChatUsable())
  const observer = new MutationObserver(updateAvailability)

  observer.observe(container, { childList: true, subtree: true })
  updateAvailability()

  return () => observer.disconnect()
}

// Waits up to one second for 8x8 to render its launcher button, then resolves availability.
export const waitFor8x8ChatButton = () =>
  new Promise<boolean>((resolve) => {
    if (isChatUsable()) {
      resolve(true)
      return
    }

    const container = document.getElementById(getButtonContainerId())

    if (!container) {
      resolve(false)
      return
    }

    let resolved = false
    let timeoutId: number
    let observer: MutationObserver
    const cleanup = () => {
      observer.disconnect()
      window.clearTimeout(timeoutId)
    }
    const finish = (chatButtonAvailable: boolean) => {
      if (resolved) return

      resolved = true
      cleanup()
      resolve(chatButtonAvailable)
    }
    observer = new MutationObserver(() => {
      if (isChatUsable()) finish(true)
    })
    timeoutId = window.setTimeout(() => finish(isChatUsable()), 1000)

    observer.observe(container, { childList: true, subtree: true })
  })

// Combines caller options with the tenant-specific 8x8 loader configuration.
const getChatConfig = (options: Chat8x8Options): Chat8x8Config => ({
  ...options,
  scriptUuid: options.scriptUuid || (options.channelName === UNAUTHENTICATED_CHAT_CHANNEL_NAME ? UNAUTHENTICATED_CHAT_SCRIPT_UUID : CHAT_SCRIPT_UUID),
  tenant: CHAT_TENANT,
  domain: DEFAULT_CHAT_DOMAIN,
  buttonContainerId: options.buttonContainerId || CHAT_BUTTON_CONTAINER_ID,
  align: 'right',
  themeCustom: {
    buttonPrimaryTextColor: '#FFFFFF',
    buttonPrimaryBackgroundColor: '#253F80',
    buttonPrimaryBackgroundColorActive: '#364e8a',
    buttonPrimaryBackgroundColorHover: '#364e8a',
    buttonPrimaryBackgroundColorDisabled: '#dee2e7',
    buttonPrimaryTextColorDisabled: '#94979b',
    buttonSecondaryTextColor: 'rgba(68, 72, 88, 1)',
    buttonSecondaryBackgroundColor: 'rgba(231, 234, 239, 1)',
    buttonSecondaryBackgroundColorActive: '#d2deeb',
    buttonSecondaryBackgroundColorHover: '#d2deeb',
    buttonSecondaryBackgroundColorDisabled: '#dee2e7',
    buttonSecondaryTextColorDisabled: '#94979b',
    buttonBorderRadius: '100px',
    headerBackgroundColor: '#253F80',
    headerTextColor: '#FFFFFF',
    chatBackgroundColor: '#FFFFFF',
    chatTextColor: '#1B1D20',
    chatLoaderColor: '#253F80',
    messageOutgoingBorderRadius: '16px 16px 0 16px',
    messageOutgoingBackgroundColor: '#253F80',
    messageOutgoingTextColor: '#FFFFFF',
    messageIncomingBorderRadius: '0 16px 16px 16px',
    messageIncomingBackgroundColor: '#EEF0F5',
    messageIncomingTextColor: '#212529',
    formLabelTextColor: '#212529',
    formElementTextColor: '#444858',
    formElementBorderColor: '#CED4DA',
    formElementBorderRadius: '6px',
    formElementBackgroundColor: '#F8F9FA',
    formElementBackgroundColorHover: '#E7EAEF',
  },
})

// Copies the loader configuration into script data attributes consumed by 8x8.
const setScriptDataset = (scriptElement: HTMLScriptElement, config: Chat8x8Config) => {
  const { dataset } = scriptElement

  Object.entries(config).forEach(([key, value]) => {
    dataset[key] = typeof value === 'string' ? value : String(value)
  })
}

// Stores the initialized 8x8 app, applies prefilled customer data, and flushes ready callbacks.
const handleChatReady = (loadedChatApp: Chat8x8App) => {
  chatApp = loadedChatApp
  chatButtonContainer = document.getElementById(getButtonContainerId())

  if (loadOptions.prefilledVars) {
    chatApp.setCustomerInfo(loadOptions.prefilledVars)
  }

  readyCallbacks.splice(0).forEach((callback) => callback(chatApp!))
}

// Resets loading state and notifies callers when 8x8 fails to initialize.
const handleChatError = (error: Event | Error) => {
  isLoading = false
  errorCallbacks.splice(0).forEach((callback) => callback(error))
}

// Resolves the regional 8x8 domain used to load the chat script.
const getChatDomain = async (config: Chat8x8Config) => {
  const url = new URL(CHAT_DISCOVERY_URL)
  url.search = new URLSearchParams({ domain: config.domain, tenant: config.tenant, channelUuid: config.channelUuid }).toString()

  try {
    const response = await fetch(url)
    const data = await response.json()

    return data.domain || config.domain
  } catch {
    return config.domain
  }
}

// Creates and injects the asynchronous 8x8 loader script into the document.
const loadChatScript = async (config: Chat8x8Config) => {
  const domain = await getChatDomain(config)
  const scriptElement = document.createElement('script')

  scriptElement.type = 'text/javascript'
  scriptElement.async = true
  scriptElement.src = config.loaderURL || `${domain}/CHAT/common/js/chatv3.js`

  setScriptDataset(scriptElement, { ...config, domain })

  scriptElement.addEventListener('init', (event) => {
    const initFn = (event as CustomEvent<{ init: (props: Chat8x8Config, callback: ReadyCallback) => void }>).detail.init
    initFn({ ...config, domain }, handleChatReady)
  })
  scriptElement.addEventListener('customerror', handleChatError)
  scriptElement.addEventListener('error', handleChatError)

  const firstScript = document.getElementsByTagName('script')[0]

  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(scriptElement, firstScript)
  } else {
    document.head.appendChild(scriptElement)
  }
}

// Loads 8x8 once, queues callbacks during initialization, and updates an existing app when reused.
export const load8x8Chat = (options: Chat8x8Options, onReady?: ReadyCallback, onError?: ErrorCallback) => {
  loadOptions = { ...loadOptions, ...options }

  // The launcher button is only ever rendered into the container element that existed when 8x8
  // finished initializing. If that element has since been replaced (e.g. the host component was
  // unmounted and remounted for a different account), the cached app can no longer show a button,
  // so discard it and reinitialize into the current container. Skip this while a chat is active so
  // an in-progress conversation isn't interrupted; it will be reinitialized once the chat ends.
  if (chatApp && !isChatActive && document.getElementById(CHAT_BUTTON_CONTAINER_ID) !== chatButtonContainer) {
    chatApp.endChat()
    document.body.classList.remove(SHOW_8X8_CHAT_CLASS)
    document.querySelectorAll('iframe[src*="vcc"]').forEach((iframe) => iframe.remove())
    chatApp = undefined
    chatButtonContainer = null
    isLoading = false
  }

  if (chatApp) {
    if (loadOptions.prefilledVars) {
      chatApp.setCustomerInfo(loadOptions.prefilledVars)
    }

    if (onReady) {
      onReady(chatApp)
    }
    return
  }

  if (onReady) {
    readyCallbacks.push(onReady)
  }

  if (onError) {
    errorCallbacks.push(onError)
  }

  if (isLoading) return

  isLoading = true
  loadChatScript(getChatConfig(options)).catch(handleChatError)
}

// Makes the 8x8 iframe visible after the user explicitly requests chat.
export const show8x8Chat = () => {
  document.body.classList.add(SHOW_8X8_CHAT_CLASS)
}

// Reveals the chat UI and starts an initialized 8x8 conversation, or maximizes it if already active.
export const start8x8Chat = () => {
  show8x8Chat()

  if (chatApp) {
    // 8x8 reopens/maximizes an in-progress conversation rather than starting a new one when called again.
    chatApp.startChat()
    isChatActive = true
  }
}

// Ends the active 8x8 conversation when one has been initialized.
export const end8x8Chat = () => {
  if (chatApp) {
    chatApp.endChat()
  }

  document.body.classList.remove(SHOW_8X8_CHAT_CLASS)
  isChatActive = false
}
